import React from 'react';
import '../CSS/StoreCreationModal.css';

const StoreCreationModal = ({ 
  isOpen, 
  onClose, 
  storeData, 
  storeOwnerCredentials, 
  isNewOwnerCreated 
}) => {
  if (!isOpen) return null;

  // Function to download data as CSV
  const downloadCSV = () => {
    const csvData = [];
    
    // Add headers
    csvData.push(['Type', 'Key', 'Value']);
    
    // Add store data
    csvData.push(['Store', 'Store ID', storeData?.store_id || '']);
    csvData.push(['Store', 'Store Name', storeData?.store?.store_name || '']);
    csvData.push(['Store', 'Store City', storeData?.store?.store_city || '']);
    csvData.push(['Store', 'Incentive Amount', `₹${storeData?.store?.incentive_amount || 0}`]);
    
    // Add store owner credentials if new owner was created
    if (isNewOwnerCreated && storeOwnerCredentials) {
      csvData.push(['Store Owner', 'Username (Email)', storeOwnerCredentials.username || '']);
      csvData.push(['Store Owner', 'Email', storeOwnerCredentials.email || '']);
      csvData.push(['Store Owner', 'Password', storeOwnerCredentials.password || '']);
      csvData.push(['Authentication', 'Auth Token', storeOwnerCredentials.auth_token || '']);
    }
    
    // Convert to CSV string
    const csvContent = csvData.map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `store_${storeData?.store_id || 'new'}_credentials.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to copy text to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      alert(`${label} copied to clipboard!`);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  return (
    <div className="store-creation-modal-overlay">
      <div className="store-creation-modal-container">
        <div className="store-creation-modal-header">
          <div className="store-creation-modal-success-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className="store-creation-modal-title">Store Created Successfully!</h2>
          <button 
            className="store-creation-modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M18 6L6 18M6 6L18 18" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="store-creation-modal-content">
          {/* Store Information */}
          <div className="store-creation-modal-section">
            <h3 className="store-creation-modal-section-title">Store Information</h3>
            <div className="store-creation-modal-info-grid">
              <div className="store-creation-modal-info-item">
                <label className="store-creation-modal-info-label">Store ID</label>
                <div className="store-creation-modal-info-value-container">
                  <span className="store-creation-modal-info-value">
                    {storeData?.store_id}
                  </span>
                  <button 
                    className="store-creation-modal-copy-btn"
                    onClick={() => copyToClipboard(storeData?.store_id, 'Store ID')}
                    title="Copy Store ID"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path 
                        d="M8 4V16C8 17.1046 8.89543 18 10 18H18C19.1046 18 20 17.1046 20 16V7.24264C20 6.97749 19.8946 6.72306 19.7071 6.53553L16.4645 3.29289C16.2769 3.10536 16.0225 3 15.7574 3H10C8.89543 3 8 3.89543 8 5Z" 
                        stroke="currentColor" 
                        strokeWidth="2"
                      />
                      <path 
                        d="M16 18V20C16 21.1046 15.1046 22 14 22H6C4.89543 22 4 21.1046 4 20V9C4 7.89543 4.89543 7 6 7H8" 
                        stroke="currentColor" 
                        strokeWidth="2"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="store-creation-modal-info-item">
                <label className="store-creation-modal-info-label">Store Name</label>
                <div className="store-creation-modal-info-value-container">
                  <span className="store-creation-modal-info-value">
                    {storeData?.store?.store_name}
                  </span>
                </div>
              </div>
              
              <div className="store-creation-modal-info-item">
                <label className="store-creation-modal-info-label">Store City</label>
                <div className="store-creation-modal-info-value-container">
                  <span className="store-creation-modal-info-value">
                    {storeData?.store?.store_city}
                  </span>
                </div>
              </div>
              
              <div className="store-creation-modal-info-item">
                <label className="store-creation-modal-info-label">Incentive Amount</label>
                <div className="store-creation-modal-info-value-container">
                  <span className="store-creation-modal-info-value">
                    ₹{storeData?.store?.incentive_amount || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Store Owner Credentials (only if new owner was created) */}
          {isNewOwnerCreated && storeOwnerCredentials && (
            <div className="store-creation-modal-section">
              <h3 className="store-creation-modal-section-title">
                Store Owner Credentials
                <span className="store-creation-modal-badge">New Account Created</span>
              </h3>
              <div className="store-creation-modal-warning">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path 
                    d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Please save these credentials securely. They won't be shown again.</span>
              </div>
              
              <div className="store-creation-modal-info-grid">
                <div className="store-creation-modal-info-item">
                  <label className="store-creation-modal-info-label">Username (Email)</label>
                  <div className="store-creation-modal-info-value-container">
                    <span className="store-creation-modal-info-value">
                      {storeOwnerCredentials.username}
                    </span>
                    <button 
                      className="store-creation-modal-copy-btn"
                      onClick={() => copyToClipboard(storeOwnerCredentials.username, 'Username')}
                      title="Copy Username"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path 
                          d="M8 4V16C8 17.1046 8.89543 18 10 18H18C19.1046 18 20 17.1046 20 16V7.24264C20 6.97749 19.8946 6.72306 19.7071 6.53553L16.4645 3.29289C16.2769 3.10536 16.0225 3 15.7574 3H10C8.89543 3 8 3.89543 8 5Z" 
                          stroke="currentColor" 
                          strokeWidth="2"
                        />
                        <path 
                          d="M16 18V20C16 21.1046 15.1046 22 14 22H6C4.89543 22 4 21.1046 4 20V9C4 7.89543 4.89543 7 6 7H8" 
                          stroke="currentColor" 
                          strokeWidth="2"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="store-creation-modal-info-item">
                  <label className="store-creation-modal-info-label">Email</label>
                  <div className="store-creation-modal-info-value-container">
                    <span className="store-creation-modal-info-value">
                      {storeOwnerCredentials.email}
                    </span>
                    <button 
                      className="store-creation-modal-copy-btn"
                      onClick={() => copyToClipboard(storeOwnerCredentials.email, 'Email')}
                      title="Copy Email"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path 
                          d="M8 4V16C8 17.1046 8.89543 18 10 18H18C19.1046 18 20 17.1046 20 16V7.24264C20 6.97749 19.8946 6.72306 19.7071 6.53553L16.4645 3.29289C16.2769 3.10536 16.0225 3 15.7574 3H10C8.89543 3 8 3.89543 8 5Z" 
                          stroke="currentColor" 
                          strokeWidth="2"
                        />
                        <path 
                          d="M16 18V20C16 21.1046 15.1046 22 14 22H6C4.89543 22 4 21.1046 4 20V9C4 7.89543 4.89543 7 6 7H8" 
                          stroke="currentColor" 
                          strokeWidth="2"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="store-creation-modal-info-item">
                  <label className="store-creation-modal-info-label">Password</label>
                  <div className="store-creation-modal-info-value-container">
                    <span className="store-creation-modal-info-value store-creation-modal-password">
                      {storeOwnerCredentials.password}
                    </span>
                    <button 
                      className="store-creation-modal-copy-btn"
                      onClick={() => copyToClipboard(storeOwnerCredentials.password, 'Password')}
                      title="Copy Password"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path 
                          d="M8 4V16C8 17.1046 8.89543 18 10 18H18C19.1046 18 20 17.1046 20 16V7.24264C20 6.97749 19.8946 6.72306 19.7071 6.53553L16.4645 3.29289C16.2769 3.10536 16.0225 3 15.7574 3H10C8.89543 3 8 3.89543 8 5Z" 
                          stroke="currentColor" 
                          strokeWidth="2"
                        />
                        <path 
                          d="M16 18V20C16 21.1046 15.1046 22 14 22H6C4.89543 22 4 21.1046 4 20V9C4 7.89543 4.89543 7 6 7H8" 
                          stroke="currentColor" 
                          strokeWidth="2"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Authentication Token (only if new owner was created) */}
          {isNewOwnerCreated && storeOwnerCredentials?.auth_token && (
            <div className="store-creation-modal-section">
              <h3 className="store-creation-modal-section-title">
                Authentication Token
                <span className="store-creation-modal-badge">For API Access</span>
              </h3>
              <div className="store-creation-modal-info-note">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path 
                    d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Use this token for API authentication with header: <code>Authorization: Token &lt;auth_token&gt;</code></span>
              </div>
              
              <div className="store-creation-modal-info-grid">
                <div className="store-creation-modal-info-item store-creation-modal-full-width">
                  <label className="store-creation-modal-info-label">Auth Token</label>
                  <div className="store-creation-modal-info-value-container">
                    <span className="store-creation-modal-info-value store-creation-modal-token">
                      {storeOwnerCredentials.auth_token}
                    </span>
                    <button 
                      className="store-creation-modal-copy-btn"
                      onClick={() => copyToClipboard(storeOwnerCredentials.auth_token, 'Auth Token')}
                      title="Copy Auth Token"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path 
                          d="M8 4V16C8 17.1046 8.89543 18 10 18H18C19.1046 18 20 17.1046 20 16V7.24264C20 6.97749 19.8946 6.72306 19.7071 6.53553L16.4645 3.29289C16.2769 3.10536 16.0225 3 15.7574 3H10C8.89543 3 8 3.89543 8 5Z" 
                          stroke="currentColor" 
                          strokeWidth="2"
                        />
                        <path 
                          d="M16 18V20C16 21.1046 15.1046 22 14 22H6C4.89543 22 4 21.1046 4 20V9C4 7.89543 4.89543 7 6 7H8" 
                          stroke="currentColor" 
                          strokeWidth="2"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="store-creation-modal-footer">
          <button 
            className="store-creation-modal-download-btn"
            onClick={downloadCSV}
            title="Download all data as CSV"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M21 15V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V15M7 10L12 15M12 15L17 10M12 15V3" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            Download CSV
          </button>
          
          <button 
            className="store-creation-modal-close-footer-btn"
            onClick={onClose}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default StoreCreationModal;